import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import { WidgetStepConfigs } from '@/screen-utils'
import { WidgetFlow, WidgetStep } from '@/types'
import { logger, resolveChatEnabled } from '@/utils'
import { createPersistOptions } from '@/utils/zustand-persist'
import {
  computeGoBack,
  computeGoForward,
  getDefaultStep,
  getFlowInitialStep,
  handleStepTransitionSideEffects,
  resolveStepRedirect,
  showNavigationError,
} from '../utils/flow-navigation'
import { track } from '@/utils/analytics'
import { useSessionStore } from './useSessionStore'
import { useWidgetStore } from './useWidgetStore'

// =============================================================================
// Types
// =============================================================================

export type FlowStateStoreState = {
  initialized: boolean
  chatWidgetEnabled: boolean

  // Flow state
  currentFlow: WidgetFlow | undefined
  currentStep: WidgetStep | undefined
  previousStep: WidgetStep | undefined
  skippedSteps: Partial<Record<WidgetStep, boolean>>
}

type FlowStateStoreActions = {
  setCurrentStep: (currentStep: WidgetStep | undefined) => void
  setCurrentFlow: (currentFlow: WidgetFlow | undefined) => void
  resetFlow: (flow: WidgetFlow) => void
  goBack: () => void
  goForward: () => void
}

export type FlowStateStore = FlowStateStoreState & FlowStateStoreActions

// =============================================================================
// Initial State
// =============================================================================

const initialState: FlowStateStoreState = {
  initialized: false,
  chatWidgetEnabled: false,
  currentStep: WidgetStep.Welcome,
  previousStep: undefined,
  currentFlow: undefined,
  skippedSteps: {},
}

// =============================================================================
// Store
// =============================================================================

export const useFlowState = create<FlowStateStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setCurrentStep: (currentStep: WidgetStep | undefined) => {
          if (currentStep === undefined) {
            useWidgetStore.getState().closeFloatingWidget()
            return
          }

          const redirectResult = resolveStepRedirect(currentStep)

          if (redirectResult.type === 'redirect') {
            logger.log('setCurrentStep -> redirect', redirectResult)
            set({ currentStep: redirectResult.step }, false, 'setCurrentStep/redirect')
            return
          }

          set({ previousStep: get().currentStep, currentStep }, false, 'setCurrentStep')
        },

        setCurrentFlow: (currentFlow: WidgetFlow | undefined) => {
          if (!currentFlow) {
            set({ currentFlow, skippedSteps: {} }, false, 'setCurrentFlow/clear')
            return
          }

          if (
            currentFlow === WidgetFlow.Chat &&
            !resolveChatEnabled(useSessionStore.getState().widgetSettings)
          ) {
            logger.warn('setCurrentFlow(Chat) ignored: chat is not enabled')
            return
          }

          const { firstStep } = getFlowInitialStep(currentFlow)
          logger.log('setCurrentFlow', currentFlow, firstStep)

          set(
            { currentFlow, currentStep: firstStep, skippedSteps: {} },
            false,
            `setCurrentFlow/${currentFlow}`
          )
        },

        goBack: () => {
          const { currentFlow, currentStep, previousStep } = get()
          const result = computeGoBack(currentFlow, currentStep, previousStep)

          set({ currentStep: result.newStep }, false, 'goBack')
        },

        goForward: () => {
          const { currentFlow, currentStep, skippedSteps } = get()
          const result = computeGoForward(currentFlow, currentStep)

          switch (result.type) {
            case 'blocked':
              showNavigationError(result.reason)
              return

            case 'terminal':
              return

            case 'success': {
              let newSkippedSteps = skippedSteps
              if (result.shouldSkip) {
                newSkippedSteps = { ...skippedSteps, [result.currentStep]: true }
              } else if (skippedSteps[result.currentStep]) {
                newSkippedSteps = { ...skippedSteps, [result.currentStep]: undefined }
              }

              handleStepTransitionSideEffects(result.nextStep)

              set(
                {
                  currentStep: result.nextStep,
                  previousStep: result.currentStep,
                  skippedSteps: newSkippedSteps,
                },
                false,
                'goForward'
              )
            }
          }
        },

        resetFlow: () => {
          const currentFlow = get().currentFlow
          if (!currentFlow) return

          const firstStep = getDefaultStep(currentFlow)
          set({ currentStep: firstStep, previousStep: undefined, skippedSteps: {} }, false, 'resetFlow')
        },
      }),
      createPersistOptions<FlowStateStore, 'currentStep' | 'currentFlow' | 'previousStep'>({
        name: 'flow-state',
        pick: ['currentStep', 'currentFlow', 'previousStep'],
        storageType: 'session',
        onHydrate: (state) => {
          if (!state?.currentStep) return

          const stepConfig = WidgetStepConfigs[state.currentStep]
          if (!stepConfig) {
            logger.log('onHydrate: Invalid persisted step, resetting to Welcome', state.currentStep)
            useFlowState.setState({ currentStep: WidgetStep.Welcome, previousStep: undefined })
            return
          }

          if (
            (state.currentStep === WidgetStep.Chat || state.currentFlow === WidgetFlow.Chat) &&
            !resolveChatEnabled(useSessionStore.getState().widgetSettings)
          ) {
            logger.log('onHydrate: Chat disabled, resetting persisted chat step to Welcome')
            useFlowState.setState({
              currentStep: WidgetStep.Welcome,
              currentFlow: undefined,
              previousStep: undefined,
            })
          }
        },
      })
    ),
    { name: 'FlowStateStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

let _previousStep: WidgetStep | undefined = useFlowState.getState().currentStep
useFlowState.subscribe((state) => {
  if (state.currentStep && state.currentStep !== _previousStep) {
    track('navigation', 'open_page', {
      step: state.currentStep,
      flow: state.currentFlow ?? '',
    })
  }
  _previousStep = state.currentStep
})
